import { bacteriaMetaConfig_9P } from '../calculate_ctvalue';
import { getBacteriaScore } from '../../utils/bacteriaEvaluator';

// =====================================================================
// Config_vitamin_b_biosynthesis.js  —  9P panel "Vitamin B biosynthesis"
// ---------------------------------------------------------------------
// Flow:
//   1. For each species, the ct value from the CSV becomes a score:
//        - if ct >= 35            -> 0            (hard override)
//        - otherwise use the label from calculate_ctvalue.js:
//            'Deficient' | 'Slightly Deficient' | 'Normal'
//          and convert it via vitaminB_9P_scoreMapping.
//   2. All species scores are summed (X).
//   3. X is compared against the conditions to get the result label:
//        X <= 0.58          -> Below optimum
//        0.58 < X < 0.75    -> Slightly below optimum
//        X >= 0.75          -> Optimum
//   4. The result is shown on page 20 (eng) / page 19 (thai).
// =====================================================================

// Species that are summed for this section.
// NOTE: these keys must match the CSV column / formData keys exactly.
const vitaminB_9P_sp = [
  'Bifidobacterium bifidum', // B. bifidum
  'Bifidobacterium longum',  // B. longum
  'Bifidobacterium lactis',  // B. lactis
  'Lactobacillus reuteri',   // L. reuteri
  'Bacteroides genus',       // bacteroides
];

// ---------------------------------------------------------------------
// Per-species LABEL -> score mapping.
// (Applied only when ct < 35; ct >= 35 always scores 0 — see helper.)
// Values taken from the provided table:
//                Deficient  Slightly deficient  Normal
//   B. bifidum   0.025      0.05                0.1
//   B. longum    0.0625     0.125               0.25
//   B. lactis    0.025      0.05                0.1
//   L. reuteri   0.025      0.05                0.1
//   bacteroides  0.1125     0.225               0.45
// ---------------------------------------------------------------------
const vitaminB_9P_scoreMapping = {
  'Bifidobacterium bifidum': {
    'Deficient': 0.025,
    'Slightly Deficient': 0.05,
    'Normal': 0.1,
  },
  'Bifidobacterium longum': {
    'Deficient': 0.0625,
    'Slightly Deficient': 0.125,
    'Normal': 0.25,
  },
  'Bifidobacterium lactis': {
    'Deficient': 0.025,
    'Slightly Deficient': 0.05,
    'Normal': 0.1,
  },
  'Lactobacillus reuteri': {
    'Deficient': 0.025,
    'Slightly Deficient': 0.05,
    'Normal': 0.1,
  },
  'Bacteroides genus': {
    'Deficient': 0.1125,
    'Slightly Deficient': 0.225,
    'Normal': 0.45,
  },
};

// ---------------------------------------------------------------------
// Result conditions + on-page position — ENGLISH version (page 20).
// X = summed score across all species above.
// Adjust the Xpos/Ypos/image values to match your PDF layout.
// ---------------------------------------------------------------------
const vitaminB_9P_condition = {
  page: 22, // eng version page (same page as macronutrient)
  XposText: 507,
  YposText: 496,
  scale_imageX: 67,
  scale_imageY: 67,
  XposImage: 88,
  YposImage: 455,
  // Bar (color) positioning — 5 bars for visual; arrow lands on matched score (1-3).
  xBarStart: 174,
  YBarStart: 502,
  xBarGap: 64,
  conditions: [
    {
      condition: (x) => x <= 0.58,
      score: 1,
      label: 'Below optimum',
      image: 'Vitamin3',
    },
    {
      condition: (x) => x > 0.58 && x < 0.75,
      score: 2,
      label: 'Slightly below optimum',
      image: 'Vitamin1',
    },
    {
      condition: (x) => x >= 0.75,
      score: 3,
      label: 'Optimum',
      image: 'Vitamin2',
    },
  ],
};

// ---------------------------------------------------------------------
// Result conditions + on-page position — THAI version (page 19).
// Same thresholds, Thai labels + Thai-page positions.
// ---------------------------------------------------------------------
const vitaminB_9P_condition_thai = {
  page: 21, // thai version page (same page as macronutrient)
  XposText: 510,
  YposText: 510,
  scale_imageX: 67,
  scale_imageY: 67,
  XposImage: 91,
  YposImage: 477,
  // Bar (color) positioning — 5 bars for visual; arrow lands on matched score (1-3).
  xBarStart: 174,
  YBarStart: 520,
  xBarGap: 64,
  conditions: [
    {
      condition: (x) => x <= 0.58,
      score: 1,
      label: 'ต่ำกว่าระดับที่เหมาะสม',
      image: 'Vitamin3',
    },
    {
      condition: (x) => x > 0.58 && x < 0.75,
      score: 2,
      label: 'ต่ำกว่าระดับที่เหมาะสมเล็กน้อย',
      image: 'Vitamin1',
    },
    {
      condition: (x) => x >= 0.75,
      score: 3,
      label: 'เหมาะสม',
      image: 'Vitamin2',
    },
  ],
};

// ---------------------------------------------------------------------
// Helper: score for one species.
//   - ct >= 35            -> 0 (hard override)
//   - otherwise: label from calculate_ctvalue.js -> score via mapping
// Returns 0 when the species/label is unknown or the value is missing.
// ---------------------------------------------------------------------
function getVitaminB9PSpeciesScore(species, formData) {
  const raw = parseFloat(formData?.[species]);
  if (isNaN(raw)) return 0;

  // hard override: ct >= 35 always scores 0
  if (raw >= 35) return 0;

  const meta = bacteriaMetaConfig_9P[species];
  if (!meta) return 0;

  // label computed by the same logic as the rest of the 9P report
  const { label } = getBacteriaScore(species, raw, formData, meta);

  const mapping = vitaminB_9P_scoreMapping[species];
  if (!mapping || mapping[label] === undefined) return 0;

  return mapping[label];
}

// ---------------------------------------------------------------------
// Main: sum all species scores and resolve the result label.
// Pass useThaiVersion=true to use the Thai config (page 19).
// Returns: { sum, label, image, score, config }
// ---------------------------------------------------------------------
function calculateVitaminB9P(formData, useThaiVersion = false) {
  let sum = 0;

  vitaminB_9P_sp.forEach((species) => {
    sum += getVitaminB9PSpeciesScore(species, formData);
  });

  // round to 4 decimals to avoid float noise (scores are small, e.g. 0.0625)
  sum = parseFloat(sum.toFixed(4));

  const config = useThaiVersion
    ? vitaminB_9P_condition_thai
    : vitaminB_9P_condition;

  const matched = config.conditions.find((c) => c.condition(sum));

  return {
    sum,
    label: matched ? matched.label : null,
    image: matched ? matched.image : null,
    score: matched ? matched.score : null,
    config,
  };
}

export {
  vitaminB_9P_sp,
  vitaminB_9P_scoreMapping,
  vitaminB_9P_condition,
  vitaminB_9P_condition_thai,
  getVitaminB9PSpeciesScore,
  calculateVitaminB9P,
};
